import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map, catchError, of } from 'rxjs';
import { AuthService } from './auth/auth.service';
import { AddressService } from './property/address.service';
import { PropertyAssessmentService } from './property/property-assessment.service';
import { UserProfileService, UserProfile } from './user-profile.service';

/** Florida = Manatee or Osceola. A missing profile/property is treated as not-FL. */
function isFlorida(profile: UserProfile | null): boolean {
  const countyCode = profile?.property?.countyCode;
  return countyCode === 'manatee' || countyCode === 'osceola';
}

/**
 * Who belongs on `/partner`. A partner lead wins regardless of state — if the partner has
 * their appeal we shouldn't file one; `isFlorida` is the fallback for a lead not yet imported.
 */
function belongsOnPartnerPage(profile: UserProfile | null): boolean {
  return profile?.partnerLead != null || isFlorida(profile);
}

export const authGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  return auth.isLoggedIn() ? true : router.createUrlTree(['/login']);
};

export const addressGuard: CanActivateFn = () => {
  const addressService = inject(AddressService);
  const router = inject(Router);
  return addressService.loadAddress().pipe(
    map(address => (address ? true : router.createUrlTree(['/error']))),
  );
};

export const assessmentGuard: CanActivateFn = () => {
  const assessmentService = inject(PropertyAssessmentService);
  const router = inject(Router);
  return assessmentService.loadAssessment().pipe(
    map(data => (data ? true : router.createUrlTree(['/error']))),
    catchError(() => of(router.createUrlTree(['/error']))),
  );
};

/**
 * Re-reads the profile from the server before routes that render from mutable profile state.
 * Must run before `nonFloridaGuard` so routing decides on fresh state; fails open to `true`.
 */
export const profileRefreshGuard: CanActivateFn = () => {
  const profileService = inject(UserProfileService);
  return profileService.refreshProfile().pipe(
    map(() => true),
    catchError(() => of(true)),
  );
};

export const profileGuard: CanActivateFn = () => {
  const profileService = inject(UserProfileService);
  const router = inject(Router);
  return profileService.loadProfile().pipe(
    map(profile => (profile ? true : router.createUrlTree(['/error']))),
    catchError(() => of(router.createUrlTree(['/error']))),
  );
};

/** Redirects users who belong on `/partner` away from the in-house case flow. Fails open to `true`. */
export const floridaPartnerGuard: CanActivateFn = () => {
  const profileService = inject(UserProfileService);
  const router = inject(Router);
  return profileService.loadProfile().pipe(
    map(profile => (belongsOnPartnerPage(profile) ? router.createUrlTree(['/partner']) : true)),
    catchError(() => of(true)),
  );
};

/** Guards `/partner` itself; everyone else, including on a load error, goes to `/confirm`. */
export const nonFloridaGuard: CanActivateFn = () => {
  const profileService = inject(UserProfileService);
  const router = inject(Router);
  return profileService.loadProfile().pipe(
    map(profile => (belongsOnPartnerPage(profile) ? true : router.createUrlTree(['/confirm']))),
    catchError(() => of(router.createUrlTree(['/confirm']))),
  );
};

export const adminGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  if (!auth.isLoggedIn()) return router.createUrlTree(['/login']);
  if (!auth.isAdmin()) return router.createUrlTree(['/']);
  return true;
};

export const rootRedirectGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (!auth.isLoggedIn()) {
    return router.createUrlTree(['/login']);
  }

  return auth.getProgress().pipe(
    map(res => {
      const step = res.currentStep || 'confirm';
      return router.createUrlTree([`/${step}`]);
    }),
    catchError(() => of(router.createUrlTree(['/confirm']))),
  );
};
